import React, {
	forwardRef,
	Ref,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';
import {
	Box,
	ButtonBase,
	CircularProgress,
	Grid2,
	IconButton,
	TextField,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import SideNavigationBar from '../../components/SideNavigationBar';
import { useMainCustomStyle } from '../../assets/theme/Theme';
import {
	ChatBotBubbleIcon,
	ChatBotIcon,
	ChatConsultant,
	DownloadIcon,
	InfoIcon,
	SendChatIcon,
} from '../../assets/icons/Icons';
import {
	downloadMediaFile,
	fetchTestChats,
	onFetchKnowledgeUploads,
	onFetchKnowledgeUploadUrls,
	onRemoveKnowledgeUpload,
	onRemoveKnowledgeUploadUrl,
	sendStudioChatMessage,
	uploadKnowledgePdf,
	uploadSelectedWebUrl,
	uploadWebUrl,
} from '../../services/ApiService';
import ChatLoader from '../../assets/images/ChatLoader.gif';
import { MAIN_THEME_COLOR, WHITE } from '../../assets/theme/Colors';
import { v4 as uuidV4 } from 'uuid';
import UploadUrlPopup from './UploadUrlPopup';
import UploadFilePopup from './UploadFilePopup';
import FileOverview from './FileOverview';
import { ChatsResponse, FeatureLimit, FeatureLimitStatus } from '../../types';
import {
	getAccessToken,
	getAgencyId,
	getFeatureLimit,
	getFeatureLimitStatus,
	getUserEmail,
	getUserId,
	getUserSessionId,
	getUserType,
	setAllFeatureLimitStatus,
	setFeatureLimit,
	setFeatureLimitStatus,
	setUserSessionId,
	setUserType,
} from '../../Utils/AuthUtil';
import {
	fetchGuestUserLimits,
	onAddClickStat,
	updateGuestUserLimits,
} from '../../services/UserService';
import LimitAlert from '../../components/LimitAlert';
import { MAX_LIMITS, TOOLTIPS, USER_TYPES } from '../../Utils/Enums';
import LimitationPopup from '../../components/LimitationPopup';
import { convertTo12HourTime, dateFormatter } from '../../Utils/Helpers';
import _ from 'lodash';
import { getMsgReadStatus } from '../../context/MainProvider';
import SmallLimitationAlert from '../../components/SmallLimitationAlert';

const INITIAL_ERROR_STATE = {
	status: false,
	msg: '',
};

const TABS = {
	doc: 'Documents',
	url: 'Websites',
};

interface Chat {
	text: string;
	isReceived?: boolean;
	position: string;
	time: string;
	fileName?: string;
	fileUrl?: string;
	caption?: string;
	chatOwner: string;
}

interface ChatRowProps {
	chat: Chat;
	onDownloadFile: (url: string) => void;
	downloadingFile: string;
	isDownloading: boolean;
}

interface ChatBubbleProps {
	data: Chat;
	onDownloadFile: (url: string) => void;
	downloadingFile: string;
	isDownloading: boolean;
}

interface ChatBodyWindowProps {
	isChatView: boolean;
	chatData: { [index: string]: Array<Chat> };
	loading: boolean;
	onDownloadFile: (url: string) => void;
	downloadingFile: string;
	isDownloading: boolean;
	isLimitReached: boolean;
}

function ChatBubble({
	data,
	onDownloadFile,
	isDownloading,
	downloadingFile,
}: ChatBubbleProps) {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	function getChatBubbleStyle() {
		if (data.position === 'left') {
			return {
				borderTopRightRadius: '24px',
				borderBottomLeftRadius: '24px',
				borderBottomRightRadius: '24px',
			};
		} else {
			return {
				borderTopLeftRadius: '24px',
				borderBottomLeftRadius: '24px',
				borderBottomRightRadius: '24px',
			};
		}
	}

	function isMessageIsTooLong() {
		const textLength = data.text.length;
		if (textLength > 40) {
			return true;
		} else {
			return false;
		}
	}

	function getChatOwnerTag(type: string) {
		if (type === 'consultant') {
			return 'Consultant';
		} else {
			return 'AI Assistant';
		}
	}

	function getChatIcon(type: string) {
		if (type === 'consultant') {
			return <ChatConsultant />;
		} else if (type === 'ai') {
			return <ChatBotBubbleIcon />;
		} else {
			return <></>;
		}
	}

	return (
		<>
			{data.position === 'left' && getChatIcon(data.chatOwner)}
			<Grid2
				component={'section'}
				sx={{
					paddingX: '20px',
					paddingY: '10px',
					marginX: '5px',
					justifyContent: 'center',
					alignItems: 'center',
					backgroundColor:
						data.position === 'left' ? '#F2F4F5' : MAIN_THEME_COLOR,
					marginTop: data.position === 'right' ? '15px' : '0px',
					width: !isMobile
						? isMessageIsTooLong()
							? '40%'
							: null
						: null,
					...getChatBubbleStyle(),
					minWidth: 0,
				}}>
				{data.position === 'left' && data.isReceived && (
					<Typography
						variant="body2"
						textAlign={'left'}
						color={MAIN_THEME_COLOR}
						fontWeight={'700'}
						paddingY={'5px'}>
						{getChatOwnerTag(data.chatOwner)}
					</Typography>
				)}
				{data.fileName && data.fileUrl ? (
					<Grid2 container flexDirection={'column'}>
						<Grid2
							container
							flexDirection={'row'}
							width={'100%'}
							alignItems={'center'}
							sx={{
								backgroundColor:
									data.position === 'left'
										? '#F0F0F0'
										: MAIN_THEME_COLOR,
								padding: '5px',
								borderRadius: '8px',
								boxShadow: '0 2px 2px rgba(0, 0, 0, 0.1)',
							}}>
							<Typography
								variant="body2"
								style={{
									overflowWrap: 'break-word',
									maxWidth: isMobile
										? 'calc(90% - 10px)'
										: 'calc(98% - 30px)',
								}}
								color={
									data.position === 'left'
										? '#000000'
										: '#FFFFFF'
								}>
								{data.fileName}
							</Typography>
							{isDownloading &&
							downloadingFile === data.fileUrl ? (
								<CircularProgress
									sx={{
										color:
											data.position === 'left'
												? '#21C17A'
												: '#FFFFFF',
										marginLeft: '5px',
										padding: '2px',
									}}
									size={24}
								/>
							) : (
								<IconButton
									sx={{
										marginLeft: '5px',
										padding: '2px',
									}}
									onClick={() =>
										onDownloadFile &&
										onDownloadFile(
											data.fileUrl ? data.fileUrl : '',
										)
									}>
									<DownloadIcon
										color={
											data.position === 'left'
												? '#000000'
												: '#FFFFFF'
										}
									/>
								</IconButton>
							)}
						</Grid2>
						<Typography
							variant="caption"
							sx={{
								color:
									data.position === 'left'
										? '#8E8E8E'
										: '#B0B0B0',
								maxWidth: '100%',
								overflowWrap: 'break-word',
								paddingTop: '5px',
								paddingLeft: '5px',
							}}>
							{data.caption}
						</Typography>
					</Grid2>
				) : (
					<Typography
						variant="body2"
						style={{
							overflowWrap: 'break-word',
							whiteSpace: 'pre-line',
						}}
						color={
							data.position === 'left' ? '#000000' : '#FFFFFF'
						}>
						{data.text}
						{!data.isReceived && data.position === 'left' && (
							<img src={ChatLoader} alt="loading..." />
						)}
					</Typography>
				)}
				<Typography
					fontSize={'8px'}
					textAlign={data.position === 'left' ? 'start' : 'end'}
					color={data.position === 'left' ? '#000000' : '#FFFFFF'}
					paddingTop={'5px'}>
					{data.time}
				</Typography>
			</Grid2>
		</>
	);
}

function ChatRow({
	chat,
	onDownloadFile,
	isDownloading,
	downloadingFile,
}: ChatRowProps) {
	return (
		<Grid2
			container
			justifyContent={
				chat.position === 'right' ? 'flex-end' : 'flex-start'
			}
			padding={'10px'}>
			<ChatBubble
				data={chat}
				onDownloadFile={onDownloadFile}
				downloadingFile={downloadingFile}
				isDownloading={isDownloading}
			/>
		</Grid2>
	);
}
const ChatBodyWindow = forwardRef(function ChatBodyWindow(
	{
		isChatView,
		chatData,
		loading,
		onDownloadFile,
		downloadingFile,
		isDownloading,
		isLimitReached,
	}: ChatBodyWindowProps,
	ref: Ref<HTMLDivElement>,
) {
	if (isChatView && !loading) {
		return (
			<Box
				ref={ref}
				sx={{
					overflowY: 'auto',
					height: isLimitReached ? '55vh' : '60vh',
					width: '100%',
					'&::-webkit-scrollbar': {
						display: 'none', // Hide the scrollbar for WebKit browsers (Chrome, Safari, Edge, etc.)
					},
					'&-ms-overflow-style:': {
						display: 'none', // Hide the scrollbar for IE
					},
				}}>
				<Grid2 padding={'10px'}>
					{Object.keys(chatData).map((item, index) => {
						return (
							<Grid2 key={index}>
								<Typography
									variant="body2"
									fontWeight={'500'}
									textAlign={'center'}>
									{dateFormatter(item)}
								</Typography>
								{chatData[item].map((chat, i) => {
									return (
										<ChatRow
											key={i}
											chat={chat}
											onDownloadFile={onDownloadFile}
											downloadingFile={downloadingFile}
											isDownloading={isDownloading}
										/>
									);
								})}
							</Grid2>
						);
					})}
				</Grid2>
			</Box>
		);
	} else if (isChatView && loading) {
		return (
			<Grid2
				container
				height="50vh"
				component={'div'}
				bgcolor={'#FFFFFF'}
				justifyContent={'center'}
				alignItems={'center'}>
				<CircularProgress sx={{ color: MAIN_THEME_COLOR }} />
			</Grid2>
		);
	} else {
		return (
			<Grid2
				container
				height={isLimitReached ? '55vh' : '60vh'}
				flexDirection={'column'}
				justifyContent={'center'}
				textAlign={'center'}
				alignItems={'center'}>
				<Typography
					fontSize={'16px'}
					color={MAIN_THEME_COLOR}
					fontWeight={'400'}>
					Test your bot
				</Typography>
				<Typography
					fontSize={'12px'}
					color={'#9CA3AF'}
					fontWeight={'400'}>
					You can test your bot by typing a message below
				</Typography>
			</Grid2>
		);
	}
});

function KnowledgeUpload() {
	const mainDivStyle = useMainCustomStyle();
	const token = getAccessToken();
	const userId = getUserId();
	const orgId = getAgencyId();
	const { fetchMsgDetails } = getMsgReadStatus();
	const limitReachedList = getFeatureLimitStatus();
	const featureLimitCounts = getFeatureLimit();
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const [chatId, setChatId] = useState<string | null>(null);
	const [message, setMessage] = useState('');
	const [messageError, setMessageError] = useState(INITIAL_ERROR_STATE);
	const [isChatWindow, setIsChatWindow] = useState(false);
	const [chatData, setChatData] = useState<{
		[index: string]: Array<Chat>;
	}>({});
	const [recentChatData, setRecentChatData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [previewUrl, setPreviewUrl] = useState('');
	const [isSendBtnDisabled, setIsSendBtnDisabled] = useState(false);
	const [uploadedFiles, setUploadedFiles] = useState<Array<string>>([]);
	const [isFileSelected, setIsFileSelected] = useState(false);
	const [isUploading, setIsUploading] = useState(false);
	const [isFileModalOpen, setIsFileModalOpen] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [url, setUrl] = useState('');
	const [urls, setUrls] = useState<Array<string>>([]);
	const [selectedUrls, setSelectedUrls] = useState<Array<string>>([]);
	const [isUrlsFetched, setIsUrlsFetched] = useState(false);
	const [urlError, setUrlError] = useState(INITIAL_ERROR_STATE);
	const [txtError, setTxtError] = useState(INITIAL_ERROR_STATE);
	const [selectedDataError, setSelectedDataError] =
		useState(INITIAL_ERROR_STATE);
	const [isUrlUploading, setIsUrlUploading] = useState(false);
	const [tab, selectedTab] = useState(TABS.doc);
	const [isTabLoading, setIsTabLoading] = useState(false);
	const [fileUploadLimitError, setFileUploadLimitError] =
		useState(INITIAL_ERROR_STATE);
	const [websiteUploadLimitError, setWebsiteUploadLimitError] =
		useState(INITIAL_ERROR_STATE);
	const [isUserHasFullAccess, setIsUserHasFullAccess] = useState(false);
	const [websiteUrlLimit, setWebsiteUrlLimit] = useState(10);
	const [websiteUrlLimitError, setWebsiteUrlLimitError] =
		useState(INITIAL_ERROR_STATE);
	const [isAlertOpen, setIsAlertOpen] = useState(false);
	const [isAlertPopupOpen, setIsAlertPopupOpen] = useState(false);
	const [isDownloading, setIsDownloading] = useState(false);
	const [downloadingFile, setDownloadingFile] = useState('');
	const [isConsultantModeEnabled, setIsConsultantModeEnabled] =
		useState(false);
	const [isFeAllLimitReached, setIsFeAllLimitReached] = useState(false);

	useLayoutEffect(() => {
		if (messagesEndRef.current) {
			messagesEndRef.current.style.scrollBehavior = 'auto';
			messagesEndRef.current.scrollTop =
				messagesEndRef.current.scrollHeight;

			requestAnimationFrame(() => {
				if (messagesEndRef.current) {
					messagesEndRef.current.style.scrollBehavior = 'smooth';
				}
			});
		}
	}, [loading, chatData]);

	useEffect(() => {
		fetchUploads();
		onFetchTestChats();
		getUserLimits();
		getChatsSessionId();
	}, []);

	async function getUserLimits() {
		if (userId && token) {
			const data = await fetchGuestUserLimits(userId, token);
			resetErrors();

			if (data.userType === USER_TYPES[0].key) {
				setUserType(USER_TYPES[0].key);
				setFeatureLimitStatus(data.isAllLimitReached);
				setAllFeatureLimitStatus({});
				setFeatureLimit({});
				setIsUserHasFullAccess(true);
				setIsAlertOpen(false);
			} else {
				setUserType(USER_TYPES[1].key);
				setFeatureLimit(data.remainingLimit);
				setAllFeatureLimitStatus(data.isAllLimitReached);
				setFeatureLimitStatus(data.reachedLimits);
				onCheckLimitations(data.reachedLimits);
				setWebsiteUrlLimit(
					MAX_LIMITS.websiteUrl - data.remainingLimit.websiteUrl,
				);
				setIsUserHasFullAccess(true);

				if (data.isAllLimitReached) {
					setIsAlertOpen(true);
					setIsFeAllLimitReached(true);
				} else {
					setIsAlertOpen(false);
					setIsFeAllLimitReached(false);
				}
			}
		}
	}

	function onCheckLimitations(limitReachedList: FeatureLimitStatus) {
		if (limitReachedList && limitReachedList.testChats) {
			setMessageError({
				status: true,
				msg: 'Your test chat session limit is reached',
			});
		}

		if (limitReachedList && limitReachedList.fileUpload) {
			setFileUploadLimitError({
				status: true,
				msg: 'Your file upload limit is reached',
			});
		}

		if (limitReachedList && limitReachedList.websiteUpload) {
			setWebsiteUploadLimitError({
				status: true,
				msg: 'Your sitemap url upload limit is reached',
			});
		}

		if (limitReachedList && limitReachedList.websiteUrl) {
			setWebsiteUrlLimitError({
				status: true,
				msg: 'Your sitemap url selection limit is reached',
			});
		}
	}

	function getChatsSessionId() {
		const id = getUserSessionId();
		if (id) {
			setChatId(id);
			return id;
		} else {
			const newId = uuidV4();
			setChatId(newId);
			setUserSessionId(newId);
			return newId;
		}
	}

	async function fetchUploads() {
		setIsTabLoading(true);
		const data = await onFetchKnowledgeUploads(
			userId ? userId : '',
			orgId ? orgId : '',
		);
		setUploadedFiles(data);
		setIsTabLoading(false);
	}

	async function fetchUploadURLs() {
		setIsTabLoading(true);
		const data = await onFetchKnowledgeUploadUrls(
			userId ? userId : '',
			orgId ? orgId : '',
		);
		setUploadedFiles(data);
		setIsTabLoading(false);
	}

	async function onFetchTestChats() {
		setLoading(true);
		const data = await fetchTestChats(
			userId ? userId : '',
			orgId ? orgId : '',
		);
		const conversation: Array<ChatsResponse> = Array.isArray(
			data?.conversation,
		)
			? data.conversation
			: [];

		if (conversation.length === 0) {
			setLoading(false);
			return;
		}

		setIsConsultantModeEnabled(data.consultant_mode);
		setChatId(data.user_id);
		setUserSessionId(data.user_id);

		const updatedData: Array<Chat> = conversation.map(item => {
			const [type, msg] = Object.entries(item)[1];
			const position = type === 'human' ? 'right' : 'left';
			const fileData = item.file ? JSON.parse(msg) : '';
			const itemFileName =
				fileData !== '' &&
				(Object.prototype.hasOwnProperty.call(fileData, 'file_name') ||
					Object.prototype.hasOwnProperty.call(fileData, 'filename'))
					? fileData.filename || fileData.file_name
					: fileData !== '' &&
						  Object.prototype.hasOwnProperty.call(fileData, 'type')
						? fileData.type.startsWith('image')
							? 'Image File'
							: 'Pdf File'
						: undefined;

			return {
				text: msg,
				position: position,
				isReceived: true,
				time: convertTo12HourTime(item.time_stamp.split(' ')[1]),
				chatOwner: type,
				date: item.time_stamp.split(' ')[0],
				fileName: itemFileName,
				fileUrl: item.file
					? `${itemFileName}+${fileData.media_id}+${fileData.type}`
					: undefined,
				caption: item.file ? fileData.caption : '',
			};
		});

		const groupByDate = _.groupBy(updatedData, 'date');

		// Order Object By Date
		const entries = _.toPairs(groupByDate);
		const sortedEntries = _.orderBy(
			entries,
			[entry => new Date(entry[0])],
			['asc'],
		);
		const sortedObj = _.fromPairs(sortedEntries);

		setChatData(sortedObj);
		setLoading(false);
		setIsChatWindow(true);
	}

	function isFullAccessUser() {
		return getUserType() === USER_TYPES[0].key && isUserHasFullAccess;
	}

	function isTestChatLimitReached() {
		return (
			!isFullAccessUser() &&
			limitReachedList &&
			limitReachedList.testChats
		);
	}

	async function onUpdateUsageLimits(
		updates: Partial<FeatureLimit>,
		isIncreasingLimit: boolean = true,
	) {
		/** if user has full access to features preventing limit updates **/
		if (isFullAccessUser()) {
			return;
		}

		if (userId && token) {
			const usedFeatures: Partial<FeatureLimit> = { ...updates };

			const response = await updateGuestUserLimits(
				userId,
				token,
				usedFeatures,
				isIncreasingLimit,
			);

			if (!response.error) {
				getUserLimits();
			}
		}
	}

	async function onSendChat() {
		if (message === '') {
			return;
		}

		if (!isFullAccessUser() && Object.keys(chatData).length === 0) {
			onUpdateUsageLimits({ testChats: 1 });
		}

		setIsSendBtnDisabled(true);
		setMessage('');
		if (!isChatWindow) {
			setIsChatWindow(true);
		}

		const currentDate = new Date();
		const todayDate = currentDate.toISOString().split('T')[0];

		let newMessages = [];

		if (isConsultantModeEnabled) {
			newMessages = [
				{
					text: message,
					chatOwner: 'human',
					time: currentDate.toLocaleTimeString([], {
						hour: '2-digit',
						minute: '2-digit',
					}),
					position: 'right',
				},
			];
		} else {
			newMessages = [
				{
					text: message,
					chatOwner: 'human',
					time: currentDate.toLocaleTimeString([], {
						hour: '2-digit',
						minute: '2-digit',
					}),
					position: 'right',
				},
				{
					text: '',
					isReceived: false,
					chatOwner: 'ai',
					time: '',
					position: 'left',
				},
			];
		}

		const isDateExist = Object.keys(chatData).includes(todayDate);
		let clone;
		if (isDateExist) {
			clone = {
				...chatData,
				[todayDate]: [...chatData[todayDate], ...newMessages],
			};
		} else {
			clone = {
				...chatData,
				[todayDate]: [...newMessages],
			};
		}

		setChatData(clone);

		const id = chatId ? chatId : getChatsSessionId();

		const responseData = await sendStudioChatMessage(
			id,
			message,
			orgId ? orgId : '',
		);

		const repliedText = responseData.error
			? responseData.message
			: responseData.result.bot_text;

		if (repliedText !== 'CONSULTANT_MODE_ON') {
			const updatedChatData = {
				...clone,
				[todayDate]: [
					...clone[todayDate].slice(0, -1),
					{
						text: repliedText,
						isReceived: true,
						chatOwner: 'ai',
						time: new Date().toLocaleTimeString([], {
							hour: '2-digit',
							minute: '2-digit',
						}),
						position: 'left',
					},
				],
			};

			setChatData(updatedChatData);
		} else {
			if (!isConsultantModeEnabled) {
				const updatedChatData = {
					...clone,
					[todayDate]: [...clone[todayDate].slice(0, -1)],
				};

				setChatData(updatedChatData);
				setIsConsultantModeEnabled(true);
			}
		}
		fetchMsgDetails();
		setIsSendBtnDisabled(false);
	}

	function onSelectAttachments(acceptedFiles: File[]) {
		setTxtError(INITIAL_ERROR_STATE);

		if (acceptedFiles[0].type !== 'application/pdf') {
			setTxtError({
				status: true,
				msg: 'Invalid file format. only pdf files are supported',
			});
			return;
		}

		if (acceptedFiles.length > 0) {
			const file = acceptedFiles[0];
			setSelectedFile(file);
			setIsFileSelected(true);
			if (file.type.startsWith('image')) {
				const preview = URL.createObjectURL(file);
				setPreviewUrl(preview);
			} else {
				setPreviewUrl('');
			}
		}
	}

	async function onUploadPdfFile() {
		if (!isFullAccessUser() && limitReachedList.fileUpload) {
			return;
		}

		if (selectedFile) {
			setIsUploading(true);
			const formData = new FormData();

			formData.append(
				'file',
				new Blob([selectedFile], { type: 'text/plain' }),
				selectedFile.name,
			);

			if (userId && orgId) {
				formData.append('userId', userId);
				formData.append('org_id', orgId);
			}

			const data = await uploadKnowledgePdf(formData);
			if (!data.error) {
				setSelectedFile(null);
				setIsFileSelected(false);
				setUploadedFiles(prevState => [
					...prevState,
					selectedFile.name,
				]);
				selectedTab(TABS.doc);
				setIsFileModalOpen(false);
				onUpdateUsageLimits({ fileUpload: 1 });
			} else {
				console.error(data.message);
				setTxtError({ status: true, msg: data.message });
			}
			setIsUploading(false);
		} else {
			setTxtError({ status: true, msg: 'Select file' });
		}

		onAddClickStats('File submit button');
	}

	function onPressNewChat() {
		const currentDate = new Date();
		const todayDate = currentDate.toISOString().split('T')[0];

		setChatData({
			[todayDate]: [
				{
					text: 'How can i help you...',
					isReceived: true,
					time: currentDate.toLocaleTimeString([], {
						hour: '2-digit',
						minute: '2-digit',
					}),
					chatOwner: 'ai',
					position: 'left',
				},
			],
		});
		const newId = uuidV4();
		setChatId(newId);
		setUserSessionId(newId);
		onUpdateUsageLimits({ testChats: 1 });
		setIsChatWindow(true);
		onAddClickStats('New chat button');
	}

	async function onPressRemoveItem(name: string) {
		const response =
			tab === TABS.doc
				? await onRemoveKnowledgeUpload(
						name,
						userId ? userId : '',
						orgId ? orgId : '',
					)
				: await onRemoveKnowledgeUploadUrl(
						name,
						userId ? userId : '',
						orgId ? orgId : '',
					);

		if (response === 'file deleted successfully!') {
			if (tab === TABS.doc) {
				onUpdateUsageLimits(
					{
						fileUpload: 1,
					},
					false,
				);
			} else {
				const websiteUCRemain =
					MAX_LIMITS.websiteUpload - featureLimitCounts.websiteUpload;
				onUpdateUsageLimits(
					{
						websiteUpload: websiteUCRemain !== 2 ? 1 : 0,
						websiteUrl: 1,
					},
					false,
				);
			}
			const clone = uploadedFiles.filter(item => item !== name);
			setUploadedFiles(clone);
			getUserLimits();
		} else {
			console.error('Error in removing data');
		}
	}

	function onChangeUrl(value: string) {
		setUrlError({ status: false, msg: '' });
		setUrl(value);
	}

	async function onSubmitWebUrl() {
		if (!isFullAccessUser() && limitReachedList.websiteUpload) {
			return;
		}

		if (isUrlsFetched) {
			onSubmitSelectedUrl();
			onAddClickStats('Sitemap submit button');
		} else {
			onSubmitFirstUrl();
		}
	}

	async function onSubmitFirstUrl() {
		if (!url || url === '') {
			setUrlError({ status: true, msg: 'Enter sitemap url' });
			return;
		}

		const parsedUrl = new URL(url);
		if (parsedUrl.protocol !== 'https:') {
			setUrlError({ status: true, msg: 'Enter website with secure url' });
			return;
		}

		setIsUrlUploading(true);

		const data = await uploadWebUrl(
			url,
			userId ? userId : '',
			orgId ? orgId : '',
		);
		if (!data.error) {
			setIsUrlsFetched(true);
			setUrls(data);
		} else {
			console.error(data.message);
			const errorMsg = data.message?.detail
				? data.message?.detail?.message.includes('not well-formed')
					? 'Error processing sitemap url (not well-formed)'
					: data.message?.detail?.message
				: data.message;

			setUrlError({
				status: true,
				msg: errorMsg,
			});
		}

		setIsUrlUploading(false);
	}

	async function onSubmitSelectedUrl() {
		if (selectedUrls.length === 0) {
			setWebsiteUrlLimitError(INITIAL_ERROR_STATE);
			setSelectedDataError({ status: true, msg: 'Select sitemap urls' });
			return;
		}

		if (!isFullAccessUser()) {
			if (websiteUrlLimit === 0) {
				setSelectedDataError(INITIAL_ERROR_STATE);
				setWebsiteUrlLimitError({
					status: true,
					msg: 'Your sitemap url selection limit is reached',
				});
				return;
			}

			const remainingCount =
				MAX_LIMITS.websiteUrl - featureLimitCounts.websiteUrl;

			if (
				featureLimitCounts.websiteUrl > 10 &&
				selectedUrls.length > remainingCount
			) {
				setWebsiteUrlLimitError(INITIAL_ERROR_STATE);
				setSelectedDataError({
					status: true,
					msg: `You have only ${remainingCount} remaining sitemap URL selections.`,
				});
				return;
			}

			if (selectedUrls.length > 10) {
				setWebsiteUrlLimitError(INITIAL_ERROR_STATE);
				setSelectedDataError({
					status: true,
					msg: `You can only select 10 sitemap url`,
				});
				return;
			}
		}

		setIsUrlUploading(true);
		const data = await uploadSelectedWebUrl(
			url,
			selectedUrls,
			userId ? userId : '',
			orgId ? orgId : '',
		);
		if (data.error) {
			console.error(data.message);
		} else {
			fetchUploadURLs();
			selectedTab(TABS.url);
			const usedFeatures = {
				websiteUpload: 1,
				websiteUrl: selectedUrls.length,
			};

			onUpdateUsageLimits(usedFeatures);
		}

		onCloseModal();
		setIsUrlUploading(false);
	}

	function onSelectItem(item: string) {
		setSelectedDataError(INITIAL_ERROR_STATE);
		if (selectedUrls.includes(item)) {
			const clone = selectedUrls.filter(value => value !== item);
			setSelectedUrls(clone);
		} else {
			setSelectedUrls(prevState => [...prevState, item]);
		}
	}

	async function onSelect(item: string) {
		setUploadedFiles([]);
		selectedTab(item);
		if (item === TABS.doc) {
			fetchUploads();
		} else {
			fetchUploadURLs();
		}
	}

	async function onDownloadFileWithUrl(url: string) {
		setIsDownloading(true);
		setDownloadingFile(url);
		const downloadData = url.split('+');
		const response = await downloadMediaFile(
			downloadData[1],
			userId ? userId : '',
			orgId ? orgId : '',
		);

		if (response.error) {
			console.error(
				'Error downloading the media file:',
				response.message,
			);
			return;
		}

		const blobData = response.data;
		const contentType =
			downloadData[2] !== '' ? downloadData[2] : 'application/pdf';
		const blob = new Blob([blobData], { type: contentType });
		const downloadLink = document.createElement('a');
		downloadLink.href = URL.createObjectURL(blob);
		downloadLink.download = downloadData[0];
		downloadLink.click();
		URL.revokeObjectURL(downloadLink.href);
		setIsDownloading(false);
		setDownloadingFile('');
	}

	function onCloseModal() {
		setIsFileModalOpen(false);
		setIsModalOpen(false);
		setUrl('');
		setSelectedFile(null);
		setUrlError({ status: false, msg: '' });
		setTxtError({ status: false, msg: '' });
		setIsUrlsFetched(false);
		setUrls([]);
		setSelectedUrls([]);
	}

	function handleClose() {
		setIsAlertOpen(false);
	}

	function onOpenDetailsPopup() {
		setIsAlertOpen(false);
		setIsAlertPopupOpen(true);
	}

	function resetErrors() {
		setMessageError(INITIAL_ERROR_STATE);
		setFileUploadLimitError(INITIAL_ERROR_STATE);
		setWebsiteUploadLimitError(INITIAL_ERROR_STATE);
		setWebsiteUrlLimitError(INITIAL_ERROR_STATE);
	}

	function onAddClickStats(item: string) {
		onAddClickStat(
			userId ? userId : '',
			item,
			true,
			'Studio',
			getUserEmail() ? getUserEmail() : 'N/A',
			'N/A',
		);
	}

	return (
		<>
			<SideNavigationBar />
			<div style={mainDivStyle}>
				<Grid2
					container
					width={'100%'}
					paddingLeft={{ xs: '20px', md: '20px', sm: '20px' }}
					paddingRight={'10px'}>
					<Grid2 container width={'100%'} paddingBottom={'10px'}>
						<Typography
							fontWeight={'700'}
							color={MAIN_THEME_COLOR}
							fontSize={'16px'}>
							Knowledge Bases
						</Typography>
					</Grid2>
					<Grid2
						container
						width={'100%'}
						flexDirection={'row'}
						columnSpacing={1}
						rowGap={1}>
						<Grid2
							size={{ xs: 12, md: 5 }}
							container
							flexDirection={'row'}
							alignItems={'center'}
							height={'80vh'}>
							<Grid2
								container
								width={'100%'}
								paddingY={'10px'}
								paddingRight={'10px'}
								alignItems={'center'}
								bgcolor={'#FFFFFF'}
								borderRadius={'5px'}
								justifyContent={'space-between'}
								flexDirection={'row'}>
								<Grid2
									container
									alignItems={'center'}
									justifyContent={'space-between'}>
									<Typography
										fontWeight={'600'}
										paddingX={'10px'}
										fontSize={'14px'}>
										Upload here
									</Typography>
								</Grid2>
								<Grid2>
									<IconButton
										disableRipple
										onClick={() => {
											setIsFileModalOpen(true);
											onAddClickStats(
												'Sitemap upload button',
											);
										}}
										sx={{
											border: `1px solid ${MAIN_THEME_COLOR}`,
											paddingX: '10px',
											color: MAIN_THEME_COLOR,
											textTransform: 'none',
											borderRadius: '5px',
											height: '40px',
											alignItems: 'center',
											width: '80px',
											marginRight: '5px',
										}}>
										<Typography
											fontWeight={'600'}
											fontSize={'14px'}
											color={MAIN_THEME_COLOR}>
											Files
										</Typography>
									</IconButton>
									<IconButton
										disableRipple
										onClick={() => {
											setIsModalOpen(true);
											onAddClickStats(
												'File upload button',
											);
										}}
										sx={{
											border: `1px solid ${MAIN_THEME_COLOR}`,
											paddingX: '10px',
											color: MAIN_THEME_COLOR,
											textTransform: 'none',
											borderRadius: '5px',
											height: '40px',
											alignItems: 'center',
										}}>
										<Typography
											fontWeight={'600'}
											fontSize={'14px'}
											color={MAIN_THEME_COLOR}>
											Websites
										</Typography>
									</IconButton>
								</Grid2>
							</Grid2>
							<Grid2
								width={'100%'}
								bgcolor={'#FFFFFF'}
								borderRadius={'5px'}
								height={'70vh'}
								marginTop={'8px'}>
								<Grid2
									container
									flexDirection={'column'}
									padding={'10px'}>
									<Grid2
										container
										borderBottom={'2px solid #E5E7EB'}
										marginBottom={'10px'}
										flexDirection={'column'}
										paddingY={'10px'}>
										<Typography
											fontWeight={'700'}
											color="#000000"
											fontSize={'14px'}>
											Knowledge uploads
										</Typography>
										<Grid2
											container
											flexDirection={'row'}
											spacing={1}
											marginTop={'10px'}>
											{Object.values(TABS).map(
												(item, index) => (
													<ButtonBase
														key={index}
														onClick={() =>
															onSelect(item)
														}
														sx={{
															backgroundColor:
																item == tab
																	? MAIN_THEME_COLOR
																	: '#F9F8F9',
															borderRadius: '5px',
															padding:
																'5px 20px 5px 20px',
														}}>
														<Typography
															fontSize={'14px'}
															color={
																item == tab
																	? WHITE
																	: '#686868'
															}>
															{item}
														</Typography>
													</ButtonBase>
												),
											)}
										</Grid2>
									</Grid2>
									<FileOverview
										data={uploadedFiles}
										onPressRemoveItem={onPressRemoveItem}
										tab={tab}
										isLoading={isTabLoading}
									/>
								</Grid2>
							</Grid2>
						</Grid2>
						<Grid2
							size={{ xs: 12, md: 7 }}
							container
							flexDirection={'column'}
							alignItems={'center'}
							sx={{
								backgroundColor: '#FFFFFF',
								borderRadius: '5px',
							}}>
							<Grid2
								container
								width={'100%'}
								justifyContent={'space-between'}
								alignItems={'center'}
								borderBottom={'1px solid #E5E7EB'}
								padding={'10px'}>
								<Grid2
									container
									alignItems={'center'}
									justifyContent={'center'}>
									<ChatBotIcon />
									<Typography
										fontWeight={'700'}
										color="#000000"
										fontSize={'14px'}
										paddingLeft="15px">
										Chat
									</Typography>
									<Tooltip
										title={TOOLTIPS.chat}
										placement={'bottom'}
										leaveTouchDelay={5000}
										enterTouchDelay={0}
										arrow>
										<Typography paddingTop={'5px'}>
											<InfoIcon />
										</Typography>
									</Tooltip>
								</Grid2>
								<IconButton
									disableRipple
									disabled={isTestChatLimitReached()}
									onClick={onPressNewChat}
									sx={{
										border: `1px solid ${MAIN_THEME_COLOR}`,
										paddingX: '10px',
										color: MAIN_THEME_COLOR,
										textTransform: 'none',
										borderRadius: '5px',
										height: '40px',
										alignItems: 'center',
									}}>
									<Typography
										fontWeight={'600'}
										fontSize={'14px'}
										color={MAIN_THEME_COLOR}>
										New Chat
									</Typography>
								</IconButton>
							</Grid2>
							<Grid2
								container
								width={'100%'}
								flexDirection={'column'}>
								<ChatBodyWindow
									isChatView={isChatWindow}
									chatData={chatData}
									ref={messagesEndRef}
									loading={loading}
									isDownloading={isDownloading}
									downloadingFile={downloadingFile}
									onDownloadFile={onDownloadFileWithUrl}
									isLimitReached={messageError.status}
								/>
								{messageError.status && (
									<SmallLimitationAlert
										content={messageError.msg}
										handleActionButtonClick={() =>
											setIsAlertPopupOpen(true)
										}
									/>
								)}
								<Grid2
									sx={{
										display: 'flex',
										minHeight: '50px',
										height: 'auto',
										alignContent: 'flex-start',
										transition: 'height 0.2s ease',
										marginX: '20px',
										marginTop: '5px',
										paddingX: '10px',
										paddingY: '5px',
										justifyContent: 'space-between',
										borderRadius: '20px',
										backgroundColor: ' #F2F4F5',
										alignItems: 'center',
									}}>
									<TextField
										type="text"
										variant="standard"
										multiline
										placeholder="Type your message here..."
										sx={{
											width: {
												md: '85%',
												xs: '82%',
											},
											'& fieldset': {
												border: 'none',
											},
										}}
										slotProps={{
											input: {
												disableUnderline: true,
											},
											htmlInput: {
												style: {
													color: '#000000',
													paddingLeft: '5px',
													fontSize: '14px',
												},
											},
										}}
										value={message}
										onChange={e =>
											setMessage(e.target.value)
										}
										onKeyDown={event => {
											const isMobile =
												'ontouchstart' in window ||
												/Mobi|Android/i.test(
													navigator.userAgent,
												);
											if (event.key === 'Enter') {
												if (
													!event.shiftKey &&
													!isMobile
												) {
													event.preventDefault();
													onSendChat();
												}
											}
										}}
										disabled={isTestChatLimitReached()}
									/>
									<IconButton
										onClick={onSendChat}
										disabled={
											message === '' ||
											isSendBtnDisabled ||
											isTestChatLimitReached()
										}>
										<SendChatIcon />
									</IconButton>
								</Grid2>
							</Grid2>
						</Grid2>
					</Grid2>
				</Grid2>
				<UploadUrlPopup
					isModalOpen={isModalOpen}
					url={url}
					urls={urls}
					selectedUrls={selectedUrls}
					isUrlsFetched={isUrlsFetched}
					urlError={urlError}
					onChange={onChangeUrl}
					onClose={onCloseModal}
					isLoading={isUrlUploading}
					onSubmit={onSubmitWebUrl}
					onSelectItem={onSelectItem}
					selectedDataError={selectedDataError}
					limitError={websiteUploadLimitError}
					urlLimitError={websiteUrlLimitError}
					urlLimitCount={websiteUrlLimit}
					onHandleAction={() => setIsAlertPopupOpen(true)}
				/>
				<UploadFilePopup
					isModalOpen={isFileModalOpen}
					onClose={onCloseModal}
					isUploading={isUploading}
					onSubmit={onUploadPdfFile}
					readTextFile={onSelectAttachments}
					selectedFile={selectedFile}
					txtError={txtError}
					limitError={fileUploadLimitError}
					onHandleAction={() => setIsAlertPopupOpen(true)}
				/>
				<LimitAlert
					isAlertOpen={isAlertOpen}
					handleClose={handleClose}
					handleActionButtonClick={onOpenDetailsPopup}
				/>
				<LimitationPopup
					open={isAlertPopupOpen}
					onClose={() => {
						setIsAlertPopupOpen(false);
						if (isFeAllLimitReached) setIsAlertOpen(true);
					}}
				/>
			</div>
		</>
	);
}

export default KnowledgeUpload;
